import { convertDiscordImageUrl } from '../utils';
import {
  IS_PRODUCTION,
  DUMDUMZ_CONTRACT_ADDRESS,
  NOT_A_REAL_ID,
  BROADKOR_CONTRACT_ADDRESS,
  BASEJUMP_BI_CONTRACT_ADDRESS,
  BASEJUMP_CONTRACT_ADDRESS,
  BROADSIDER_OG_CONTRACT_ADDRESS
} from '../constants';

const getMetadata = async (tokenId) => {
  const metadataUrl = `https://4shc66ds38.execute-api.us-east-1.amazonaws.com/metadata/${tokenId}`;
  const response = await fetch(metadataUrl);
  if (!response.ok) {
    return {};
  }
  return await response.json();
};

export default class FigureModel {
  constructor(alchemyNftData, isMock = true, isSideload = false) {
    if (isMock) {
      this.nftData = alchemyNftData;
      this.nftData.fullImage = '/mocks/figures/full_mock.png';
      this.nftData.isReal = false;
      this.nftData.isSideload = isSideload;
      this.nftData.attributes = [];
    } else {
      const glbPath = alchemyNftData.rawMetadata.vrm.split('_').slice(-1)[0].replace('.vrm', '.glb');
      this.nftData = {
        basejumpAvatarId: alchemyNftData.avatarId,
        isHenchie: alchemyNftData.rawMetadata.avatar_type === 'chad',
        id: alchemyNftData.tokenId,
        contractAddress: alchemyNftData.contractAddress,
        attributes: alchemyNftData.rawMetadata.attributes.map(a => ({ name: a.trait_type, value: a.value})),
        image: alchemyNftData.rawMetadata.image,
        name: alchemyNftData.rawMetadata.name,
        vrm: alchemyNftData.rawMetadata.vrm,
        baselineStats: alchemyNftData.rawMetadata.baselineStats,
        permanentModifiers: alchemyNftData.rawMetadata.permanentModifiers,
        temporaryModifiers: alchemyNftData.rawMetadata.temporaryModifiers,
        tokenUri: alchemyNftData.tokenUri.raw,
        isReal: false, //true
        isSideload,
        syncNeeded: alchemyNftData.rawMetadata.sync_needed,
        glb: alchemyNftData.rawMetadata.glb || `https://4shc66ds38.execute-api.us-east-1.amazonaws.com/glb/${glbPath}`,
        fullImage: alchemyNftData.rawMetadata.image_full,
        thumb: alchemyNftData.media[0].thumbnail
      }
    }
  }

  getBaselineStats() {
    return {
      healthPoints: this.nftData.baselineStats.health_points,
      attackPoints: this.nftData.baselineStats.attack_points,
      defencePoints: this.nftData.baselineStats.defence_points,
      speed: this.nftData.baselineStats.speed,
      criticalChance: this.nftData.baselineStats.critical_chance
    };
  }
  
  getPermanentModifiers() {
    return {
      healthPoints: this.nftData.permanentModifiers.health_points,
      attackPoints: this.nftData.permanentModifiers.attack_points,
      defencePoints: this.nftData.permanentModifiers.defence_points,
      healthMultiplier: this.nftData.permanentModifiers.health_multiplier,
      attackMultiplier: this.nftData.permanentModifiers.attack_multiplier,
      defenceMultiplier: this.nftData.permanentModifiers.defence_multiplier,
      criticalChancePoints: this.nftData.permanentModifiers.critical_chance_points,
      speedPoints: this.nftData.permanentModifiers.speed_points,
      criticalChanceMultiplier: this.nftData.permanentModifiers.critical_chance_multiplier,
      speedMultiplier: this.nftData.permanentModifiers.speed_multiplier
    };
  }

  getTemporaryModifiers() {
    return {
      healthPoints: this.nftData.temporaryModifiers.health_points,
      attackPoints: this.nftData.temporaryModifiers.attack_points,
      defencePoints: this.nftData.temporaryModifiers.defence_points,
      healthMultiplier: this.nftData.temporaryModifiers.health_multiplier,
      attackMultiplier: this.nftData.temporaryModifiers.attack_multiplier,
      defenceMultiplier: this.nftData.temporaryModifiers.defence_multiplier,
      criticalChancePoints: this.nftData.temporaryModifiers.critical_chance_points,
      speedPoints: this.nftData.temporaryModifiers.speed_points,
      criticalChanceMultiplier: this.nftData.temporaryModifiers.critical_chance_multiplier,
      speedMultiplier: this.nftData.temporaryModifiers.speed_multiplier
    };
  }

  static parseAttributes(basejumpData) {
    const attributes = [
      { trait_type: 'Name', value: basejumpData.name },
      { trait_type: 'Health', value: basejumpData.baseline_stats.health_points + (basejumpData.permanent_modifiers?.health_points || 0) },
      { trait_type: 'Attack', value: basejumpData.baseline_stats.attack_points + (basejumpData.permanentModifiers?.attack_points || 0) },
      { trait_type: 'Defence', value: basejumpData.baseline_stats.defence_points + (basejumpData.permanent_modifiers.defence_points || 0)},
      { trait_type: 'Critical Chance', value: basejumpData.baseline_stats.critical_chance + (basejumpData.permanent_modifiers.critical_chance_points || 0)},
      { trait_type: 'Speed', value: basejumpData.baseline_stats.speed + (basejumpData.permanent_modifiers.speed_points || 0)},
    ];
    return attributes;
  }

  static fromBasejumpAvatar(avatarData, convertForDiscord = false) {
    // Build fake Alchemy NFT Data
    const fakeNftData = {
      tokenUri: { raw: '' },
      avatarId: avatarData.id,
      contractAddress: avatarData.contract_address,
      rawMetadata: {
        avatar_type: avatarData.avatar_type,
        sync_needed: !!avatarData.sync_needed,
        name: avatarData.name,
        image: convertDiscordImageUrl(avatarData.image_url, convertForDiscord),
        vrm: avatarData.vrm_url,
        attributes: [...avatarData.traits, ...FigureModel.parseAttributes(avatarData)],
        baselineStats: avatarData.baseline_stats,
        permanentModifiers: avatarData.permanent_modifiers,
        temporaryModifiers: avatarData.temporary_modifiers,
        image_full: convertDiscordImageUrl(avatarData.image_url, convertForDiscord),
        glb: avatarData.glb_url
      },
      media: [{ thumbnail: convertDiscordImageUrl(avatarData.thumbnail_url, convertForDiscord) }],
      tokenId: avatarData.token_id
    }
    return new FigureModel(fakeNftData, false);
  }

  getContractAddress() {
    return this.nftData.contractAddress;
  }

  getAvatarId() {
    return this.nftData.basejumpAvatarId;
  }

  async getEquippedItemIds() {
    if (!this.isReal()) {
      return [];
    }
    const response = await fetch(this.nftData.tokenUri);
    const data = await response.json();
    const equippedIds = data.equipment.map((i) => i.token_id);
    return equippedIds;
  }

  getTokenId() {
    return this.nftData.id;
  }

  isReal() {
    return !!this.nftData.isReal;
  }

  isSideload() {
    return !!this.nftData.isSideload;
  }

  isHenchie() {
    return !!this.nftData.isHenchie;
  }

  static fromAlchemyNft(alchemyNftData, isMock = true, isSideload = false) {
    return new FigureModel(alchemyNftData, isMock, isSideload);
  }

  static async _fromOgNft(alchemyNftData) {
    const rawMetadata = await getMetadata(alchemyNftData.id);
    rawMetadata.image = alchemyNftData.image;
    rawMetadata.vrm = alchemyNftData.vrm;
    alchemyNftData.tokenId = alchemyNftData.id;
    alchemyNftData.rawMetadata = rawMetadata;
    alchemyNftData.tokenUri = { raw: '' };
    alchemyNftData.media = [{ thumbnail: alchemyNftData.thumbnail }];
    return new FigureModel(alchemyNftData, false);
  }

  static async _fromKorus(alchemyNftData) {
    const rawMetadata = {
      name: 'BROADKOR',
      attributes: []
    };
    rawMetadata.image = alchemyNftData.image;
    rawMetadata.vrm = alchemyNftData.vrm;
    alchemyNftData.tokenId = alchemyNftData.id;
    alchemyNftData.rawMetadata = rawMetadata;
    alchemyNftData.tokenUri = { raw: '' };
    alchemyNftData.media = [{ thumbnail: alchemyNftData.thumbnail }];
    return new FigureModel(alchemyNftData, false);
  }
  
  static async _fromDumdumz(alchemyNftData) {
    const rawMetadata = {
      name: 'DumDumz',
      attributes: []
    };
    rawMetadata.image = alchemyNftData.image;
    rawMetadata.vrm = alchemyNftData.vrm;
    alchemyNftData.tokenId = alchemyNftData.id;
    alchemyNftData.rawMetadata = rawMetadata;
    alchemyNftData.tokenUri = { raw: '' };
    alchemyNftData.media = [{ thumbnail: alchemyNftData.thumbnail }];
    return new FigureModel(alchemyNftData, false);
  }

  static async fromOgNft(alchemyNftData) {
    if (alchemyNftData.contract.toLowerCase() === BROADKOR_CONTRACT_ADDRESS.toLowerCase()) {
      return FigureModel._fromKorus(alchemyNftData);
    }
    if (alchemyNftData.contract.toLowerCase() === DUMDUMZ_CONTRACT_ADDRESS.toLowerCase()) {
      return FigureModel._fromDumdumz(alchemyNftData);
    }
    
    return FigureModel._fromOgNft(alchemyNftData);
  }

  getScaleFactor() {
    return this.nftData.scaleFactor || 1;
  }

  getTitle() {
    return this.nftData.name;
  }

  getId() {
    return this.nftData.id;
  }

  getName() {
    const name = this.nftData.name.split('-').pop();
    return name.trim();
  }

  isOg() {
    const tokenId = parseInt(this.nftData.id);
    return tokenId <= 7290 && this.nftData?.contractAddress?.toLowerCase() === BROADSIDER_OG_CONTRACT_ADDRESS.toLowerCase();
  }

  canDownloadVrm() {
    if (this.isOg() && !this.nftData.syncNeeded) {
      return false;
    }
    return true;
  }
  getAvatarImgSource() {
    return this.nftData.image;
  }
  
  getAvatarThumbSource() {
    return this.nftData.thumb || this.nftData.image;
  }

  getFullImgSource() {
    return this.nftData.fullImage;
  }

  getGlbSource() {
    return this.nftData.glb;
  }

  getVrmSource() {
    return this.nftData.vrm;
  }

  async sendAssetTo(assetId, receiverFigure, walletClient, wagmiConfig, notifyStep) {
    throw new Error('Transfer not yet enabled');
  }

  getOpenSeaLink() {
    return IS_PRODUCTION
      ? `https://opensea.io/assets/base/${BASEJUMP_CONTRACT_ADDRESS}/${this.getId()}`
      : `https://testnets.opensea.io/assets/base-sepolia/${BASEJUMP_CONTRACT_ADDRESS}/${this.getId()}`;
  }

  getTraits() {
    return this.nftData.attributes;
  }
}
